// Product catalog data for pools. Also some helper functions related to the data structure.

/*
    Notes on "Stable IDs"

    Because of the integration with a CMS/translation platform, we want data objects to have constant 
    identifiers. We use a human-readable string format. This Vue project can be considered the source of truth.
    The stable IDs are also used in between the Vue app and the PlayCanvas app. 
    
        Entities with stable IDs:
            pool model (tag property, here)
            accessory item (tag property in Storyblok)
            category (tag property, here)
            series (tag property, here)
            product option (the property names from the object in poolCustomization.js)
            option choice (id property, here)

    This pattern was finalized later in the project. Earlier, string IDs were used for some entities, 
    but not all. There's not a naming convention, because some IDs were grandfathered in from how the 
    project was before. So, for example, texture choice IDs happen to match the English label, and dimension 
    choice IDs happen to match the (imperial system) measurement. 

    Note: the dimension choice ID gets converted into a numeric type and used as a number in a few places, in
    the Vue project and in the PlayCanvas project. So, it is important to continue with the pattern of the 
    dimension choice ID matching the (imperial system) measurement, for now. 
*/

const allLinerChoices = [
    {
        id: 'Light Blue',
        displayName: 'Light Blue',
        image: 'light-blue.jpg',
        cost: 0
    },
    {
        id: 'Siesta Wave',
        displayName: 'Siesta Wave',
        image: 'siesta-wave.jpg',
        cost: 0
    },
    {
        id: 'Dark Blue',
        displayName: 'Dark Blue',
        image: 'dark-blue.jpg',
        cost: 0
    },
    {
        id: 'Diffusion',
        displayName: 'Diffusion',
        image: 'diffusion.jpg',
        cost: 0
    },
    {
        id: 'Electric Aquarius',
        displayName: 'Electric Aquarius',
        image: 'diffuse-streamline.png',
        cost: 0
    },
    {
        id: 'Gray Marino',
        displayName: 'Gray Marino',
        image: 'gray-marino.jpg',
        cost: 0
    },
];

// all original pools except streamline
const mostOriginalLiner = allLinerChoices.filter(d => d.id != 'Electric Aquarius');
// streamline
const streamlineLiner = allLinerChoices.filter(d => d.id == 'Electric Aquarius');

const allSystemChoices = [
    {
        id: 'Sapphire',
        displayName: 'Sapphire',
        image: 'sapphire.jpg',
        cost: 0
    },
    {
        id: 'Platinum',
        displayName: 'Platinum',
        image: 'platinum.jpg',
        cost: 0
    }
];

// system choices for most original pools
const mostOriginalSystem = allSystemChoices;
const onlySaphireSystem = allSystemChoices.filter(d => d.id == 'Sapphire');

const allCopingChoices = [
    {
        id: 'Arctic Ice',
        displayName: 'Arctic Ice',
        image: 'arctic-ice.jpg',
        cost: 0
    },
    {
        id: 'Desert Plateau',
        displayName: 'Desert Plateau',
        image: 'desert-plateau.jpg',
        cost: 0
    },
    {
        id: 'Summit Gray',
        displayName: 'Summit Gray',
        image: 'summit-gray.jpg',
        cost: 0
    },
];

const allSKirtingChoices = [
    {
        id: 'Riverbed Brown',
        displayName: 'Riverbed Brown',
        image: 'riverbed.jpg',
        cost: 0
    },
    {
        id: 'Weathered Gray',
        displayName: 'Weathered Gray',
        image: 'weathered-gray.jpg',
        cost: 0
    },
    {
        id: 'Mocha',
        displayName: 'Mocha',
        image: 'mocha.jpg',
        cost: 0
    },
    {
        id: 'Gray',
        displayName: 'Gray',
        image: 'gray.jpg',
        cost: 0
    },
];
// all modular pools except streamline
const mostOriginalSkirting = allSKirtingChoices.filter(d => d.id == 'Riverbed Brown' || d.id == 'Weathered Gray');
// swim spa pools
const swimSpaSkirting = allSKirtingChoices.filter(d => d.id == 'Mocha' || d.id == 'Gray');

const allShellChoices = [
    {
        id: 'Alpine White',
        displayName: 'Alpine White',
        image: 'alpine-white.jpg',
        cost: 0
    },
    {
        id: 'Ice Gray',
        displayName: 'Ice Gray',
        image: 'ice-gray.jpg',
        cost: 0
    },
];

const allInstallChoices = [
    {
        id: 'Above Ground',
        displayName: 'Above Ground',
        icon: 'pool-installed-above.svg'
    },
    {
        id: 'Partial In-Ground',
        displayName: 'Partial In-Ground',
        icon: 'pool-installed-partial.svg'
    },
    {
        id: 'In-Ground',
        displayName: 'In-Ground',
        icon: 'pool-installed-in.svg'
    }
];
const aboveGroundOnly = allInstallChoices.filter(d => d.id == 'Above Ground');

const locationChoices =  [ 
    {
        id: 'INDOOR',
        displayName: 'Indoor',
        icon: 'indoors-icon.svg'
    },
    {
        id: 'OUTDOOR',
        displayName: 'Outdoor',
        icon: 'outdoors-icon.svg'
    },
];
// category > series > pool
// Note: the Original category didn't multiple have series (before 2025) so it has a single dummy series
const poolData =
[
  {
    tag: 'custom',
    title: 'Modular Pools',
    description: 'Choose which model suits you, then select the size & accessories.',
    modelsOnly: true,
    series:
    [
        {
            tag: 'pro',
            title: 'Pro Series',
            // popupTitle: 'E-Series Swim Spas',
            description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
            // shorthand: 'e',
            models:
            [
                {
                    tag: 'ep508pro', 
                    title: 'EP508 Pro', 
                    // subtitle: '', 
                    // description: '',
                    handle: 'ep508pro',
                    price: 65000,
                    options: {
                          width: { choices: [ { id: '8', measurement: '8' } ], unit: "'" },
                          len: { choices: [ { id: '15', measurement: '15' } ], unit: "'", },
                          depth: { 
                            default: '48', 
                            choices: [ { id: '48', measurement: '48' }, { id: '54', measurement: '54' } ], 
                            unit: '"', 
                          },
                          install: { choices: allInstallChoices },
                          location: { choices: locationChoices },
                          skirting: { choices: mostOriginalSkirting },
                          liner: { choices: mostOriginalLiner },
                          system: { choices: mostOriginalSystem },
                          coping: { choices: allCopingChoices },
                    },
                    includedAcc: ['manual-security-cover','underwater-lights','pace','fit-at-home','mirror'],
                    addOnAcc: ['automatic-security-cover','aqua-bike','swim-mirror','treadmill','hydrotherapy-jets','remote-pool-monitoring','corner-step','interior-entry-steps','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
            ]
        },
        {
            tag: 'plus',
            title: 'Plus Series',
            // popupTitle: 'E-Series Swim Spas',
            description: 'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.',
            // shorthand: 'e',
            models:
            [
                {
                    tag: 'ep515plus', 
                    title: 'EP515 Plus', 
                    // subtitle: '', 
                    // description: '',
                    handle: 'ep515plus',
                    price: 60000,
                    options: {
                          width: { choices: [ { id: '15', measurement: '15' } ], unit: "'" },
                          len: { choices: [ { id: '15', measurement: '15' } ], unit: "'", },
                          depth: { 
                            default: '48', 
                            choices: [ { id: '48', measurement: '48' }, { id: '54', measurement: '54' } ], 
                            unit: '"', 
                          },
                          install: { choices: allInstallChoices },
                          location: { choices: locationChoices },
                          skirting: { choices: mostOriginalSkirting },
                          liner: { choices: mostOriginalLiner },
                          system: { choices: mostOriginalSystem },
                          coping: { choices: allCopingChoices },
                    },
                    includedAcc: ['manual-security-cover','underwater-lights','pace','fit-at-home'],
                    addOnAcc: ['automatic-security-cover','aqua-bike','swim-mirror','mirror','treadmill','hydrotherapy-jets','remote-pool-monitoring','corner-step','interior-entry-steps','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
                {
                    tag: 'ep508plus', 
                    title: 'EP508 Plus', 
                    // subtitle: '', 
                    // description: '',
                    handle: 'ep508plus',
                    price: 55000,
                    options: {
                          width: { choices: [ { id: '8', measurement: '8' } ], unit: "'" },
                          len: { choices: [ { id: '15', measurement: '15' } ], unit: "'", },
                          depth: { 
                            default: '48', 
                            choices: [ { id: '48', measurement: '48' }, { id: '54', measurement: '54' } ], 
                            unit: '"', 
                          },
                          install: { choices: allInstallChoices },
                          location: { choices: locationChoices },
                          skirting: { choices: mostOriginalSkirting },
                          liner: { choices: mostOriginalLiner },
                          system: { choices: mostOriginalSystem },
                          coping: { choices: allCopingChoices },
                    },
                    includedAcc: ['manual-security-cover','two-underwater-lights','led-bench-lights','pace','fit-at-home'],
                    addOnAcc: ['automatic-security-cover','aqua-bike','swim-mirror','mirror','treadmill','hydrotherapy-jets','remote-pool-monitoring','corner-step','interior-entry-steps','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
                {
                    tag: 'ep407plus', 
                    title: 'EP407 Plus', 
                    // subtitle: '', 
                    // description: '',
                    handle: 'ep407plus',
                    price: 45000,
                    options: {
                          width: { choices: [ { id: '7', measurement: '7' } ], unit: "'" },
                          len: { choices: [ { id: '14', measurement: '14' } ], unit: "'", },
                          depth: { 
                            default: '48', 
                            choices: [ { id: '48', measurement: '48' }, { id: '54', measurement: '54' } ], 
                            unit: '"', 
                          },
                          install: { choices: allInstallChoices },
                          location: { choices: locationChoices },
                          skirting: { choices: mostOriginalSkirting },
                          liner: { choices: mostOriginalLiner },
                          system: { choices: mostOriginalSystem },
                          coping: { choices: allCopingChoices },
                    },
                    includedAcc: ['manual-security-cover','two-underwater-lights','pace','fit-at-home'],
                    addOnAcc: ['automatic-security-cover','aqua-bike','swim-mirror','mirror','treadmill','hydrotherapy-jets','remote-pool-monitoring','corner-step','interior-entry-steps','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
            ],
        },
        {
            tag: 'ep',
            title: 'EP Series',
            // popupTitle: 'E-Series Swim Spas',
            description: 'Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
            // shorthand: 'e',
            models:
            [
                {
                    tag: 'ep510', 
                    title: 'EP510', 
                    // subtitle: '', 
                    // description: '',
                    handle: 'ep510',
                    price: 50000,
                    options: {
                          width: { choices: [ { id: '10', measurement: '10' } ], unit: "'" },
                          len: { choices: [ { id: '15', measurement: '15' } ], unit: "'", },
                          depth: { 
                            default: '48', 
                            choices: [ { id: '48', measurement: '48' }, { id: '54', measurement: '54' } ], 
                            unit: '"', 
                          },
                          install: { choices: allInstallChoices },
                          location: { choices: locationChoices },
                          skirting: { choices: mostOriginalSkirting },
                          liner: { choices: mostOriginalLiner },
                          system: { choices: mostOriginalSystem },
                          coping: { choices: allCopingChoices },
                    },
                    includedAcc: ['manual-security-cover','two-underwater-lights','led-bench-lights','pace','fit-at-home'],
                    addOnAcc: ['automatic-security-cover','aqua-bike','swim-mirror','mirror','treadmill','hydrotherapy-jets','remote-pool-monitoring','corner-step','interior-entry-steps','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
                {
                    tag: 'ep508', 
                    title: 'EP508', 
                    // subtitle: '', 
                    // description: '',
                    handle: 'ep508',
                    price: 45000,
                    options: {
                          width: { choices: [ { id: '8', measurement: '8' } ], unit: "'" },
                          len: { choices: [ { id: '15', measurement: '15' } ], unit: "'", },
                          depth: { 
                            default: '48', 
                            choices: [ { id: '48', measurement: '48' }, { id: '54', measurement: '54' } ], 
                            unit: '"', 
                          },
                          install: { choices: allInstallChoices },
                          location: { choices: locationChoices },
                          skirting: { choices: mostOriginalSkirting },
                          liner: { choices: mostOriginalLiner },
                          system: { choices: mostOriginalSystem },
                          coping: { choices: allCopingChoices },
                    },
                    includedAcc: ['manual-security-cover','two-underwater-lights','led-bench-lights','pace','fit-at-home'],
                    addOnAcc: ['automatic-security-cover','aqua-bike','swim-mirror','mirror','treadmill','hydrotherapy-jets','remote-pool-monitoring','corner-step','interior-entry-steps','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
                {
                    tag: 'ep407', 
                    title: 'EP407', 
                    // subtitle: '', 
                    // description: '',
                    handle: 'ep407',
                    price: 35000,
                    options: {
                          width: { choices: [ { id: '7', measurement: '7' } ], unit: "'" },
                          len: { choices: [ { id: '14', measurement: '14' } ], unit: "'", },
                          depth: { 
                            default: '48', 
                            choices: [ { id: '48', measurement: '48' }, { id: '54', measurement: '54' } ], 
                            unit: '"', 
                          },
                          install: { choices: allInstallChoices },
                          location: { choices: locationChoices },
                          skirting: { choices: mostOriginalSkirting },
                          liner: { choices: mostOriginalLiner },
                          system: { choices: mostOriginalSystem },
                          coping: { choices: allCopingChoices },
                    },
                    includedAcc: ['manual-security-cover','two-underwater-lights','pace','fit-at-home'],
                    addOnAcc: ['automatic-security-cover','aqua-bike','swim-mirror','mirror','treadmill','hydrotherapy-jets','remote-pool-monitoring','corner-step','interior-entry-steps','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
                {
                    tag: 'ep207', 
                    title: 'EP207', 
                    handle: 'ep207',
                    price: 27500,
                    options: {
                          width: { choices: [ { id: '7', measurement: '7' } ], unit: "'" },
                          len: { choices: [ { id: '12', measurement: '12' } ], unit: "'", },
                          depth: { 
                            default: '48', 
                            choices: [ { id: '48', measurement: '48' }, { id: '54', measurement: '54' } ], 
                            unit: '"', 
                          },
                          install: { choices: allInstallChoices },
                          location: { choices: locationChoices },
                          liner: { choices: mostOriginalLiner },
                          system: { choices: mostOriginalSystem },
                    },
                    includedAcc: ['manual-security-cover','pace','fit-at-home'],
                    addOnAcc: ['automatic-security-cover','aqua-bike','swim-mirror','mirror','treadmill','hydrotherapy-jets','remote-pool-monitoring','corner-step','interior-entry-steps','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
            ],
        },
        {
            tag: 'sp',
            title: 'SP Series',
            // popupTitle: 'E-Series Swim Spas',
            description: 'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.',
            // shorthand: 'e',
            models:
            [
                {
                    tag: 'sp105',
                    title: 'SP105',
                    // popupTitle: 'Streamline Pool',
                    description: '',
                    // shorthand: 'streamline',
                    handle: 'sp105',
                    price: 19999,
                    options: {
                        len: { 
                            default: '14', 
                            choices: [{ id: '14', measurement: '14' }], 
                            unit: "'" 
                        },
                        width:  { 
                            default: '6', 
                            choices: [{id: '6',measurement: '6'}], 
                            unit: "'" 
                        },
                        depth: { 
                            default: '48', 
                            choices: [{id: '48',measurement: '48'}], 
                            unit: '"' 
                        },
                        install: { choices: aboveGroundOnly },
                        location: { choices: locationChoices },
                        liner: { choices: streamlineLiner },
                        system: { choices: onlySaphireSystem },
                    },
                    includedAcc: ['manual-security-cover','corner-step'],
                    addOnAcc: ['tether-streamline','aqua-bike','mirror','remote-pool-monitoring','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
            ],
        },
        {
            tag: 'plunge',
            title: 'Plunge Series',
            // popupTitle: 'E-Series Swim Spas',
            description: 'Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.',
            // shorthand: 'e',
            models:
            [
                {
                    tag: 'plungemax', 
                    title: 'Plunge Max', 
                    // subtitle: '', 
                    // description: '',
                    handle: 'plungemax',
                    price: 35000,
                    options: {
                          width: { choices: [ { id: '10', measurement: '10' } ], unit: "'" },
                          len: { choices: [ { id: '15', measurement: '15' } ], unit: "'", },
                          depth: { 
                            default: '48', 
                            choices: [ { id: '48', measurement: '48' }, { id: '54', measurement: '54' } ], 
                            unit: '"', 
                          },
                          install: { choices: allInstallChoices },
                          location: { choices: locationChoices },
                          skirting: { choices: mostOriginalSkirting },
                          liner: { choices: mostOriginalLiner },
                          system: { choices: mostOriginalSystem },
                          coping: { choices: allCopingChoices },
                    },
                    includedAcc: ['manual-security-cover','underwater-lights'],
                    addOnAcc: ['automatic-security-cover','aqua-bike','treadmill','hydrotherapy-jets','remote-pool-monitoring','corner-step','interior-entry-steps','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
                {
                    tag: 'plungeprime', 
                    title: 'Plunge Prime', 
                    // subtitle: '', 
                    // description: '',
                    handle: 'plungeprime',
                    price: 26000,
                    options: {
                          width: { choices: [ { id: '7', measurement: '7' } ], unit: "'" },
                          len: { choices: [ { id: '10', measurement: '10' } ], unit: "'", },
                          depth: { 
                            default: '48', 
                            choices: [ { id: '48', measurement: '48' }, { id: '54', measurement: '54' } ], 
                            unit: '"', 
                          },
                          install: { choices: allInstallChoices },
                          location: { choices: locationChoices },
                          skirting: { choices: mostOriginalSkirting },
                          liner: { choices: mostOriginalLiner },
                          system: { choices: mostOriginalSystem },
                          coping: { choices: allCopingChoices },
                    },
                    includedAcc: ['manual-security-cover','two-underwater-lights'],
                    addOnAcc: ['automatic-security-cover','aqua-bike','treadmill','hydrotherapy-jets','remote-pool-monitoring','corner-step','interior-entry-steps','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
                {
                    tag: 'plungemini', 
                    title: 'Plunge Mini', 
                    // subtitle: '', 
                    // description: '',
                    handle: 'plungemini',
                    price: 18000,
                    options: {
                          width: { choices: [ { id: '7', measurement: '7' } ], unit: "'" },
                          len: { choices: [ { id: '7', measurement: '7' } ], unit: "'", },
                          depth: { 
                            default: '48', 
                            choices: [ { id: '48', measurement: '48' }, { id: '54', measurement: '54' } ], 
                            unit: '"', 
                          },
                          install: { choices: allInstallChoices },
                          location: { choices: locationChoices },
                          skirting: { choices: mostOriginalSkirting },
                          liner: { choices: mostOriginalLiner },
                          system: { choices: mostOriginalSystem },
                          coping: { choices: allCopingChoices },
                    },
                    includedAcc: ['manual-security-cover','two-underwater-lights'],
                    addOnAcc: ['automatic-security-cover','aqua-bike','treadmill','hydrotherapy-jets','remote-pool-monitoring','corner-step','interior-entry-steps','2-tier-step','3-tier-step','4-tier-step','heavy-4-tier-step']
                },
            ],
        },
    ]
  },
  {
    tag: 'spa',
    title: 'Swim Spas',
    description: 'Choose your preferred swim spa series, then select the model & accessories.',
    modelsOnly: false,
    series:
    [
      {
          tag: 'e',
          title: 'E-Series Swim Spas',
        //   popupTitle: 'E-Series Swim Spas',
          description: 'Designed with premium options and our signature Hydrodrive® current. Meet your fitness goals … and  have fun surpassing them!',
        //   shorthand: 'e',
          models:
          [
              {
                  tag: 'e2000', title: 'E2000', subtitle: 'E-Series Swim Spa 20\' model', description: 'With its large fitness space and Hydrodrive™ current, combined with separate temperature-controlled hot tub, enjoy swimming and fitness followed by hot water relaxation.',
                  handle: 'E2000',
                  price: 70999,
                  options: {
                        len: { choices: [ { id: '20', measurement: '20' } ], unit: "'", },
                        width: { choices: [ { id: '89', measurement: '89' } ], unit: '"' },
                        depth: { choices: [ { id: '58', measurement: '58' } ], unit: '"', },
                        install: { choices: allInstallChoices },
                        location: { choices: locationChoices },
                        skirting: { choices: swimSpaSkirting },
                        shell: { choices: allShellChoices },
                  },
                  addOnAcc: ['treadmill', 'mirror', 'pace', 'tether', 'resistance-bands', 'bluetooth-sound-system', 'fit-at-home', 'gecko', 'signature-steps', 'watkins-cover-set', 'easy-slider', 'covercradle-lifter', 'watkins-lifter', 'swimdek', 'exercise-kit']
              },
              {
                  tag: 'e700', title: 'E700', subtitle: 'E-Series Swim Spa 17\' model', description: 'Perfectly suited for swimmers and families who enjoy a workout with our Hydrodrive™ current followed by jetted hydromassage.',
                  handle: 'E700',
                  price: 58999,
                  options: {
                        len: { choices: [ { id: '17', measurement: '17' } ], unit: "'", },
                        width: { choices: [ { id: '89', measurement: '89' } ], unit: '"' },
                        depth: { choices: [ { id: '58', measurement: '58' } ], unit: '"' },
                        install: { choices: allInstallChoices },
                        location: { choices: locationChoices },
                        skirting: { choices: swimSpaSkirting },
                        shell: { choices: allShellChoices },
                  },
                  addOnAcc: ['treadmill', 'mirror', 'pace', 'tether', 'resistance-bands', 'bluetooth-sound-system', 'fit-at-home', 'gecko', 'signature-steps', 'watkins-cover-set', 'easy-slider', 'covercradle-lifter', 'watkins-lifter', 'swimdek', 'exercise-kit']
              },
              {
                  tag: 'e550', title: 'E550', subtitle: 'E-Series Swim Spa 15\' model with extended width', description: 'With our Hydrodrive™ current, no seating and a wider exercise area, you get more room to swim and exercise.',
                  handle: 'E550',
                  price: 55999,
                  options: {
                        len: { choices: [ { id: '15', measurement: '15' } ], unit: "'", },
                        width: { choices: [ { id: '94', measurement: '94' } ], unit: '"' },
                        depth: { choices: [ { id: '58', measurement: '58' } ], unit: '"', },
                        install: { choices: allInstallChoices },
                        location: { choices: locationChoices },
                        skirting: { choices: swimSpaSkirting },
                        shell: { choices: allShellChoices },
                  },
                  addOnAcc: ['treadmill', 'mirror', 'pace', 'tether', 'resistance-bands', 'bluetooth-sound-system', 'fit-at-home', 'gecko', 'signature-steps', 'watkins-cover-set', 'easy-slider', 'covercradle-lifter', 'watkins-lifter', 'swimdek', 'exercise-kit']
              },
              {
                  tag: 'e500', title: 'E500', subtitle: 'E-Series Swim Spa 15\' model', description: 'The ideal combination of swimming or exercise with our Hydrodrive™ current and invigorating hydromassage.',
                  handle: 'E500',
                  price: 52999,
                  options: {
                        len: { choices: [ { id: '15', measurement: '15' } ], unit: "'", },
                        width: { choices: [{ id: '89', measurement: '89' } ], unit: '"' },
                        depth: { choices: [ { id: '58', measurement: '58' } ], unit: '"' },
                        install: { choices: allInstallChoices },
                        location: { choices: locationChoices },
                        skirting: { choices: swimSpaSkirting },
                        shell: { choices: allShellChoices },
                  },
                  addOnAcc: ['treadmill', 'mirror', 'pace', 'tether', 'resistance-bands', 'bluetooth-sound-system', 'fit-at-home', 'gecko', 'signature-steps', 'watkins-cover-set', 'easy-slider', 'covercradle-lifter', 'watkins-lifter', 'swimdek', 'exercise-kit']
              },
          ],
      },
      {
          tag: 'x',
          title: 'X-Series Swim Spas',
        //   popupTitle: 'X-Series Swim Spas',
          description: 'Everything you need to stay active, have fun, and achieve your health goals. Our 4-jet SwimCross® current far exceeds the performance of other jetted swim spas.',
        //   shorthand: 'x',
          models:
          [
              {
                  tag: 'x2000', title: 'X2000', subtitle: 'X-Series Swim Spa 20\' model', description: 'Enjoy Dual temperature controls: Stay active in the fitness area, then recover in the spa area!',
                  handle: 'X2000',
                  price: 56999,
                  options: {
                        len: { choices: [ { id: '20', measurement: '20' } ], unit: "'", },
                        width: { choices: [ { id: '89', measurement: '89' } ], unit: '"' },
                        depth: { choices: [ { id: '58', measurement: '58' } ], unit: '"', },
                        install: { choices: allInstallChoices },
                        location: { choices: locationChoices },
                        skirting: { choices: swimSpaSkirting },
                        shell: { choices: allShellChoices },
                  },
                  addOnAcc: ['mirror', 'tether', 'resistance-bands', 'bluetooth-sound-system', 'gecko', 'signature-steps', 'watkins-cover-set', 'easy-slider', 'covercradle-lifter', 'watkins-lifter', 'swimdek', 'led-lighting', 'exercise-kit']
              },
              {
                  tag: 'x500', title: 'X500', subtitle: 'X-Series Swim Spa 15\' model', description: 'Our variable and smooth jetted SwimCross® current lets you exercise with gentle resistance and unwind in hydromassage seats.',
                  handle: 'X500',
                  price: 36999,
                  options: {
                        len: { choices: [ { id: '15', measurement: '15' } ], unit: "'", },
                        width: { choices: [ { id: '89', measurement: '89' } ], unit: '"' },
                        depth: { choices: [ { id: '50', measurement: '50' } ], unit: '"', },
                        install: { choices: allInstallChoices },
                        location: { choices: locationChoices },
                        skirting: { choices: swimSpaSkirting },
                        shell: { choices: allShellChoices },
                  },
                  addOnAcc: ['mirror', 'tether', 'resistance-bands', 'bluetooth-sound-system', 'gecko', 'signature-steps', 'watkins-cover-set', 'easy-slider', 'covercradle-lifter', 'watkins-lifter', 'swimdek', 'led-lighting', 'exercise-kit']
              },
          ],
      },
      {
          tag: 'r',
          title: 'R-Series Swim Spas',
        //   popupTitle: 'R-Series Swim Spas',
          description: 'Designed for family fun, pool parties, and aquatic workouts with our 3-jet RecSport® current. Enjoy quality, versatility, and exceptional value.',
        //   shorthand: 'r',
          models:
          [
              {
                  tag: 'r500', title: 'R500', subtitle: 'R-Series Swim Spa 15\' model', description: 'Families get more room to play, workout, and unwind in this budget-friendly swim spa.',
                  handle: 'R500',
                  price: 30999,
                  options: {
                        len: { choices: [ { id: '15', measurement: '15' } ], unit: "'", },
                        width: { choices: [ { id: '89', measurement: '89' } ], unit: '"' },
                        depth: { choices: [ { id: '50', measurement: '50' } ], unit: '"', },
                        install: { choices: allInstallChoices },
                        location: { choices: locationChoices },
                        skirting: { choices: swimSpaSkirting },
                        shell: { choices: allShellChoices },
                  },
                  addOnAcc: ['mirror', 'tether', 'resistance-bands', 'bluetooth-sound-system', 'gecko', 'signature-steps', 'watkins-cover-set', 'easy-slider', 'covercradle-lifter', 'watkins-lifter', 'swimdek', 'led-lighting', 'exercise-kit']
              },
              {
                  tag: 'r220', title: 'R220', subtitle: 'R-Series Swim Spa 12\' model', description: "With bench seating and three sculpted spa seats, this budget-friendly, 12' swim spa is perfect for exercise, relaxation, and entertaining.",
                  handle: 'R220',
                  price: 26999,
                  options: {
                        len: { choices: [ { id: '12', measurement: '12' } ], unit: "'", },
                        width: { choices: [ { id: '89', measurement: '89' } ], unit: '"' },
                        depth: { choices: [ { id: '50', measurement: '50' } ], unit: '"', },
                        install: { choices: allInstallChoices },
                        location: { choices: locationChoices },
                        skirting: { choices: swimSpaSkirting },
                        shell: { choices: allShellChoices },
                  },
                  addOnAcc: ['mirror', 'tether', 'resistance-bands', 'bluetooth-sound-system', 'gecko', 'signature-steps', 'watkins-cover-set', 'easy-slider', 'covercradle-lifter', 'watkins-lifter', 'swimdek', 'exercise-kit']
              },
              {
                  tag: 'r200', title: 'R200', subtitle: 'R-Series Swim Spa 12\' model', description: 'Our most affordable swim spa is family-friendly, with 3 hydromassage seats and room for 10!',
                  handle: 'R200',
                  price: 25499,
                  options: {
                        len: { choices: [ { id: '12', measurement: '12' } ], unit: "'", },
                        width: { choices: [ { id: '89', measurement: '89' } ], unit: '"' },
                        depth: { choices: [ { id: '50', measurement: '50' } ], unit: '"', },
                        install: { choices: allInstallChoices },
                        location: { choices: locationChoices },
                        skirting: { choices: swimSpaSkirting },
                        shell: { choices: allShellChoices },
                  },
                  addOnAcc: ['mirror', 'tether', 'resistance-bands', 'bluetooth-sound-system', 'gecko', 'signature-steps', 'watkins-cover-set', 'easy-slider', 'covercradle-lifter', 'watkins-lifter', 'swimdek', 'led-lighting', 'exercise-kit']
              },
          ],
      },
    ]
  }
];

// some helpers that go along with this data structure

const categoryOfPool = modelTag => {
    // loop through all categories
    for (let i = 0; i < poolData.length; i++) {
        // loop through all series in the category
        for (let j = 0; j < poolData[i].series.length; j++) {
            // loop through all models in the series
            for (let k = 0; k < poolData[i].series[j].models.length; k++) {
                // if the model tag matches the one we are looking for
                if (poolData[i].series[j].models[k].tag === modelTag) {
                    // return the category object
                    return poolData[i];
                }
            }
        }
    }
    console.log('could not find category of model ' + modelTag);
    return null;
};
const seriesOfPool = modelTag => {
    for (let i = 0; i < poolData.length; i++) {
        for (let j = 0; j < poolData[i].series.length; j++) {
            for (let k = 0; k < poolData[i].series[j].models.length; k++) {
                if (poolData[i].series[j].models[k].tag === modelTag) {
                    // return the identifier of the category
                    return poolData[i].series[j];
                }
            }
        }
    }
    console.log('could not find category of model ' + modelTag);
    return null;
};
const getModelByHandle = handle => {
    // all categories
    let categoryList = [ ...poolData ];
    // all series
    let seriesList = categoryList.flatMap(category => category.series);
    // all models
    let modelList = seriesList.flatMap(series => series.models);
    return modelList.find(model => model.tag.toLowerCase() === handle.toLowerCase());
};

export {poolData, categoryOfPool, seriesOfPool, getModelByHandle};
